import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { Check, Plus, X } from 'lucide-react';
import { resolveEntityColor, entityColorWash } from '../../utils/entityColor';
import './PropertyPillField.css';

/**
 * Notion-style pill field for select and multi-select properties.
 * 
 * Resting: Shows selected value(s) as colored pill badges.
 * Active:  Click pill → inline dropdown with colored option list.
 * 
 * Props:
 * - type: 'select' | 'multi-select'
 * - value: string (select) or string[] (multi-select) — option IDs
 * - options: [{ id, label, color }]
 * - onChange: (newValue) => void
 * - onCreateOption: (label) => { id, label, color } — called when user creates a new option
 * - placeholder: string (default "Empty")
 * - readOnly: boolean
 */
export default function PropertyPillField({
    type = 'select',
    value,
    options = [],
    onChange,
    onCreateOption,
    placeholder = 'Empty',
    readOnly = false,
}) {
    const [isOpen, setIsOpen] = useState(false);
    const [search, setSearch] = useState('');
    const containerRef = useRef(null);
    const searchRef = useRef(null);

    const isMulti = type === 'multi-select';
    const currentValues = useMemo(() => {
        if (isMulti) return Array.isArray(value) ? value : [];
        return value ? [value] : [];
    }, [value, isMulti]);

    // Close on outside click
    useEffect(() => {
        if (!isOpen) return;
        const handler = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setIsOpen(false);
                setSearch('');
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, [isOpen]);

    // Auto-focus search on open
    useEffect(() => {
        if (isOpen && searchRef.current) {
            searchRef.current.focus();
        }
    }, [isOpen]);

    // Close on Escape
    const handleKeyDown = useCallback((e) => {
        if (e.key === 'Escape') {
            setIsOpen(false);
            setSearch('');
        }
    }, []);

    // Filter options by search
    const filteredOptions = useMemo(() => {
        if (!search.trim()) return options;
        const q = search.toLowerCase();
        return options.filter(o => o.label.toLowerCase().includes(q));
    }, [options, search]);

    // Check if search term exactly matches an existing option
    const exactMatchExists = useMemo(() => {
        if (!search.trim()) return true;
        const q = search.trim().toLowerCase();
        return options.some(o => o.label.toLowerCase() === q);
    }, [options, search]);

    // Select / deselect an option
    const handleSelect = (optionId) => {
        if (isMulti) {
            const current = Array.isArray(value) ? value : [];
            if (current.includes(optionId)) {
                onChange(current.filter(v => v !== optionId));
            } else {
                onChange([...current, optionId]);
            }
        } else {
            // Single select: toggle off or select
            onChange(value === optionId ? '' : optionId);
            setIsOpen(false);
            setSearch('');
        }
    };

    // Create a new option and auto-select it
    const handleCreateOption = () => {
        if (!onCreateOption || !search.trim()) return;
        const newOption = onCreateOption(search.trim());
        if (newOption) {
            handleSelect(newOption.id);
            setSearch('');
        }
    };

    // Remove a specific value (multi-select pill ×)
    const handleRemove = (e, optionId) => {
        e.stopPropagation();
        if (isMulti) {
            onChange(currentValues.filter(v => v !== optionId));
        } else {
            onChange('');
        }
    };

    // Get pill styles for an option
    const getPillStyle = (option) => {
        const color = resolveEntityColor(option?.color, '#6B7280');
        return {
            backgroundColor: entityColorWash(color, 12),
            color: color,
            borderColor: entityColorWash(color, 25),
        };
    };

    // Open the dropdown
    const handleOpen = () => {
        if (readOnly) return;
        setIsOpen(true);
    };

    return (
        <div className="pill-field" ref={containerRef} onKeyDown={handleKeyDown}>
            {/* ===== Resting State: Pills or Empty ===== */}
            <div onClick={handleOpen} style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-xxs)', alignItems: 'center', minHeight: 'var(--size-lg)', cursor: readOnly ? 'default' : 'pointer', width: '100%' }}>
                {currentValues.length > 0 ? (
                    currentValues.map(val => {
                        const opt = options.find(o => o.id === val);
                        if (!opt) return null;
                        return (
                            <span
                                key={val}
                                className="pill-field__pill"
                                style={getPillStyle(opt)}
                            >
                                {opt.label}
                                {!readOnly && isOpen && (
                                    <button
                                        className="btn btn-ghost btn-sm btn-icon pill-field__pill-remove"
                                        onClick={(e) => handleRemove(e, val)}
                                        title="Remove"
                                    >
                                        <X size={10} />
                                    </button>
                                )}
                            </span>
                        );
                    })
                ) : (
                    <span className="pill-field__empty">{placeholder}</span>
                )}
            </div>

            {/* ===== Dropdown ===== */}
            {isOpen && (
                <div className="pill-dropdown">
                    {/* Search */}
                    <div className="pill-dropdown__search">
                        <input
                            ref={searchRef}
                            className="pill-dropdown__search-input"
                            type="text"
                            placeholder={onCreateOption ? "Select an option or create one" : "Search options..."}
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            onKeyDown={e => {
                                if (e.key === 'Enter' && search.trim() && !exactMatchExists && onCreateOption) {
                                    e.preventDefault();
                                    handleCreateOption();
                                }
                            }}
                        />
                    </div>

                    {/* Options */}
                    <div className="pill-dropdown__options">
                        {filteredOptions.length > 0 ? (
                            filteredOptions.map(opt => {
                                const isSelected = currentValues.includes(opt.id);
                                return (
                                    <button
                                        key={opt.id}
                                        className={`pill-dropdown__option ${isSelected ? 'pill-dropdown__option--selected' : ''}`}
                                        onClick={() => handleSelect(opt.id)}
                                    >
                                        {/* Drag handle dots (visual only) */}
                                        <span className="pill-dropdown__option-drag">
                                            <span className="pill-dropdown__option-drag-dot" />
                                            <span className="pill-dropdown__option-drag-dot" />
                                            <span className="pill-dropdown__option-drag-dot" />
                                        </span>

                                        {/* Colored pill label */}
                                        <span
                                            className="pill-dropdown__option-pill"
                                            style={getPillStyle(opt)}
                                        >
                                            {opt.label}
                                        </span>

                                        {/* Checkmark for selected */}
                                        <span className="pill-dropdown__option-check">
                                            {isSelected && <Check size={14} />}
                                        </span>
                                    </button>
                                );
                            })
                        ) : null}

                        {/* Create new option button */}
                        {search.trim() && !exactMatchExists && onCreateOption && (
                            <button
                                className="pill-dropdown__create-option"
                                onClick={handleCreateOption}
                            >
                                <Plus size={14} />
                                <span>Create</span>
                                <span className="pill-dropdown__create-option-label">
                                    {search.trim()}
                                </span>
                            </button>
                        )}

                        {/* Empty state — only show when no options AND no create available */}
                        {filteredOptions.length === 0 && !(search.trim() && !exactMatchExists && onCreateOption) && (
                            <div className="pill-dropdown__empty">
                                {search.trim() ? 'No matching options' : 'No options available'}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

